import type { LeviathanCaptain, LeviathanDerived } from "@personnel/rules-leviathan";
import { captainFieldValues, templateFor } from "./fieldMap.js";

/**
 * Fills the Captain's Log for the captain's faction.
 *
 * pdf-lib is loaded on demand: it is roughly the size of the rest of the app,
 * and most sessions never export a PDF. The templates are 2-3 MB each and are
 * likewise only fetched when asked for.
 */
export async function fillCaptainsLog(
  captain: LeviathanCaptain,
  derived: LeviathanDerived,
): Promise<Blob> {
  if (captain.faction === null) {
    throw new Error("Choose a faction — each one has its own Captain's Log.");
  }

  const [{ PDFDocument, PDFBool, PDFName }, response] = await Promise.all([
    import("pdf-lib"),
    // Templates keep a stable filename, so unlike the content-hashed bundle
    // there is nothing in the URL to invalidate a cached copy -- a corrected
    // template would otherwise be ignored indefinitely. "no-cache" revalidates
    // rather than refetching: nginx answers 304 from the ETag when the file is
    // unchanged, so this costs a round trip, not 2.5 MB.
    fetch(templateFor(captain.faction), { cache: "no-cache" }),
  ]);

  if (!response.ok) {
    throw new Error(`Could not load the Captain's Log template (${response.status}).`);
  }

  const pdf = await PDFDocument.load(await response.arrayBuffer());
  const form = pdf.getForm();
  const values = captainFieldValues(captain, derived);

  // A renamed or removed field would otherwise throw halfway through, leaving
  // a half-filled form. Collect the failures and report them together.
  const missing: string[] = [];
  for (const [name, value] of Object.entries(values)) {
    try {
      form.getTextField(name).setText(value);
    } catch {
      missing.push(name);
    }
  }

  if (missing.length > 0) {
    throw new Error(
      `This template is missing ${missing.length} expected field${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}.`,
    );
  }

  // Every field on the form is set to "/Consolas 0 Tf" -- Consolas, auto-sized
  // to its box. Left to itself pdf-lib discards that and redraws each field in
  // Helvetica at a fixed size, so the filled form no longer matches the blank
  // one. Setting NeedAppearances and skipping pdf-lib's appearance pass leaves
  // the original strings intact for the viewer to render.
  form.acroForm.dict.set(PDFName.of("NeedAppearances"), PDFBool.True);

  // Deliberately not flattened. The Captain's Log is filled in during play,
  // so the mission log and trackers must stay editable.
  const bytes = await pdf.save({ updateFieldAppearances: false });
  return new Blob([bytes.slice().buffer], { type: "application/pdf" });
}
